import React, { useMemo } from "react";
import { Card, Col, Descriptions, Row, Table, Tag } from "antd";
import dayjs from "dayjs";
import { FormInstance } from "antd/lib/form";
import { helper } from "@src/controls/controlHelper";

interface ReviewSectionProps {
  form: FormInstance;
  pageId?: number;
}

const formatTime = (v?: any) => {
  if (!v && v !== 0) return "-";
  const d = typeof v === "number" ? dayjs(v) : dayjs(String(v));
  return d.isValid() ? d.format("YYYY-MM-DD HH:mm") : String(v);
};

const ReviewSection: React.FC<ReviewSectionProps> = ({ form, pageId }) => {
  const { envent, ticket = [] } = useMemo(() => {
    const values = form.getFieldsValue(true) || {};
    return {
      envent: values.envent || {},
      ticket: Array.isArray(values.ticket) ? values.ticket : [],
    } as any;
  }, [form]);
  const IdName: React.FC<{ api: string; id?: any; pageId?: number }> = ({ api, id, pageId: _pageId }) => {
    const [text, setText] = React.useState<string>("-");
    React.useEffect(() => {
      let mounted = true;
      (async () => {
        try {
          if (id === undefined || id === null) {
            if (mounted) setText("-");
            return;
          }
          const pageInfo = await helper.getPage(_pageId || pageId || 120);
          const rs: any = await helper.callPageApi(pageInfo, api, {
            queryInput: JSON.stringify({ id }),
            select: "id,name",
          });
          const item = rs?.data?.data?.[0];
          if (mounted) setText(item ? `${item.name}` : String(id));
        } catch (e) {
          if (mounted) setText(String(id));
        }
      })();
      return () => {
        mounted = false;
      };
    }, [api, id, _pageId]);
    return <span>{text}</span>;
  };

  const eventMeta = Array.isArray(envent?.metaProperties)
    ? envent.metaProperties
    : [];

  return (
    <Row gutter={[12, 12]}>
      <Col span={24}>
        <Card type="inner" title="Thông tin sự kiện">
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Ảnh sự kiện" span={2}>
              {envent?.image ? (
                <img
                  src={envent.image}
                  alt="event"
                  style={{ maxHeight: 120, maxWidth: "100%", objectFit: "contain" }}
                />
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Banner" span={2}>
              {(Array.isArray(envent?.banner) ? envent.banner : envent?.banner ? [envent.banner] : []).length > 0 ? (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {(Array.isArray(envent?.banner) ? envent.banner : [envent?.banner]).map((b: any, idx: number) => (
                    <img
                      key={String(idx)}
                      src={b}
                      alt={`banner-${idx}`}
                      style={{ height: 64, width: 96, objectFit: "cover", borderRadius: 4 }}
                    />
                  ))}
                </div>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Tên sự kiện">
              {envent?.nameEvent || "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Trạng thái">
              {typeof envent?.status === 'boolean' ? (
                envent.status ? <Tag color="green">active</Tag> : <Tag color="red">inactive</Tag>
              ) : (
                "-"
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Bắt đầu sự kiện">
              {formatTime(envent?.startDateEvent)}
            </Descriptions.Item>
            <Descriptions.Item label="Kết thúc sự kiện">
              {formatTime(envent?.endDateEvent)}
            </Descriptions.Item>
            <Descriptions.Item label="Bắt đầu bán vé">
              {formatTime(envent?.presaleStart)}
            </Descriptions.Item>
            <Descriptions.Item label="Kết thúc bán vé">
              {formatTime(envent?.presaleEnd)}
            </Descriptions.Item>
            <Descriptions.Item label="Sân vận động">
              <IdName api='find_stadium' id={envent?.stadiumId} pageId={pageId} />
            </Descriptions.Item>
            <Descriptions.Item label="Layout vé">
              <IdName api='find_ticketLayout' id={envent?.ticketLayoutId} pageId={pageId} />
            </Descriptions.Item>
            <Descriptions.Item label="Tối đa / đơn">
              {envent?.maxItemsPerOrder ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Giữ vé (s)">
              {envent?.reservationTime ?? "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Giờ check-in">
              {formatTime(envent?.checkInTime)}
            </Descriptions.Item>
            <Descriptions.Item label="Kênh bán vé" span={2}>
              {(Array.isArray(envent?.salesChannels)
                ? envent.salesChannels
                : []
              ).map((c: string) => (
                <Tag key={c}>{c}</Tag>
              ))}
            </Descriptions.Item>
            <Descriptions.Item label="Mô tả" span={2}>
              {envent?.description ? (
                <div
                  style={{ maxHeight: 240, overflow: 'auto' }}
                  dangerouslySetInnerHTML={{ __html: String(envent.description) }}
                />
              ) : (
                "-"
              )}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>

      <Col span={24}>
        <Card type="inner" title="Meta properties sự kiện (HOME, VISITOR, LEAGUE)">
          <Table
            size="small"
            pagination={false}
            rowKey={(r: any, idx) => String(idx)}
            dataSource={eventMeta}
            columns={[
              { title: "Thuộc tính", dataIndex: "name", key: "name", width: 200 },
              { title: "Giá trị", dataIndex: "value", key: "value" },
            ]}
          />
        </Card>
      </Col>

      <Col span={24}>
        <Card type="inner" title="Danh sách vé">
          <Table
            size="small"
            pagination={false}
            rowKey={(r: any, idx) => String(idx)}
            dataSource={ticket}
            columns={[
              {
                title: "Ảnh vé",
                key: "image",
                width: 110,
                render: (_: any, r: any) =>
                  r?.image ? (
                    <img
                      src={r.image}
                      alt="ticket"
                      style={{ height: 48, width: 72, objectFit: "cover", borderRadius: 4 }}
                    />
                  ) : (
                    "-"
                  ),
              },
              { title: "Tên vé", dataIndex: "name", key: "name", width: 200 },
              { title: "Giá bán", dataIndex: "price", key: "price", width: 120, render: (v: any) => (typeof v === 'number' ? v.toLocaleString() : v) },
              { title: "Giá gốc", dataIndex: "originPrice", key: "originPrice", width: 120, render: (v: any) => (typeof v === 'number' ? v.toLocaleString() : v) },
              { title: "Số lượng", dataIndex: "quantity", key: "quantity", width: 110 },
              {
                title: "Thời gian bán",
                key: "time",
                render: (_: any, r: any) => `${formatTime(r?.startDateSell)} → ${formatTime(r?.endDateSell)}`,
              },
              {
                title: "Kích hoạt",
                dataIndex: "status",
                key: "status",
                width: 110,
                render: (v: any) => (v ? <Tag color="green">active</Tag> : <Tag color="red">inactive</Tag>),
              },
              {
                title: "Min/Max / đơn",
                key: "mm",
                width: 150,
                render: (_: any, r: any) => `${r?.minItemsPerOrder ?? "-"}/${r?.maxItemsPerOrder ?? "-"}`,
              },
              {
                title: "Mô tả",
                dataIndex: "description",
                key: "description",
                render: (v: any) =>
                  v ? (
                    <div
                      style={{ maxHeight: 120, overflow: 'auto' }}
                      dangerouslySetInnerHTML={{ __html: String(v) }}
                    />
                  ) : (
                    '-'
                  ),
              },
            ]}
          />
        </Card>
      </Col>

      <Col span={24}>
        <Card type="inner" title="Metadata theo từng vé">
          <Table
            size="small"
            pagination={false}
            rowKey={(r: any, idx) => String(idx)}
            dataSource={ticket}
            columns={[
              { title: "Vé", key: "ticket", width: 220, render: (_: any, r: any, idx: number) => r?.name || `Vé #${idx + 1}` },
              {
                title: "Metadata",
                key: "metadata",
                render: (_: any, r: any) => {
                  const data = Array.isArray(r?.metaData) ? r.metaData : [];
                  return (
                    <Table
                      size="small"
                      pagination={false}
                      rowKey={(x: any) => `${String(x?.name ?? "")}--${String(x?.value ?? "")}`}
                      dataSource={data}
                      columns={[
                        { title: "Thuộc tính", dataIndex: "name", key: "name", width: 200 },
                        {
                          title: "Giá trị",
                          dataIndex: "value",
                          key: "value",
                          render: (v: any, rr: any) => {
                            const s = String(rr?.name || '').toUpperCase();
                            if (s === 'GATE') return <IdName api='find_checkinGate' id={v} pageId={pageId} />;
                            return v;
                          },
                        },
                      ]}
                    />
                  );
                },
              },
            ]}
          />
        </Card>
      </Col>
    </Row>
  );
};

export default ReviewSection;


